import React, { useState } from 'react';
import { X, DownloadCloud, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { axiosInstance } from '../App';

/**
 * One-click migration modal — pulls a full backup from any source
 * environment (preview URL + admin creds) and restores it into the
 * current environment. Destructive by default (wipes then restores).
 */
const MigrateFromPreviewModal = ({ open, onClose, onSuccess }) => {
  // "pull" = fetch data FROM another env INTO this one (default when
  // running on production). "push" = send THIS env's data OUT to
  // another env (default when running on a preview URL). Detected
  // from window.location.host so the user rarely needs to switch.
  const isPreview = typeof window !== 'undefined'
    && /preview|preview\./i.test(window.location.host);
  const [mode, setMode] = useState(isPreview ? 'push' : 'pull');
  const [sourceUrl, setSourceUrl] = useState('');
  const [email, setEmail] = useState('admin@test.com');
  const [password, setPassword] = useState('');
  const [wipeBeforeImport, setWipeBeforeImport] = useState(true);
  const [includeUploads, setIncludeUploads] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [result, setResult] = useState(null);

  if (!open) return null;

  const reset = () => {
    setResult(null);
    setSourceUrl('');
    setPassword('');
  };

  const handleClose = () => {
    if (migrating) return;
    reset();
    onClose?.();
  };

  const runMigration = async () => {
    const cleanUrl = sourceUrl.trim().replace(/\/+$/, '');
    const isPushMode = mode === 'push';
    if (!cleanUrl || !email.trim() || !password) {
      toast.error('أدخل جميع الحقول: الرابط، البريد، وكلمة المرور');
      return;
    }
    if (!/^https?:\/\//i.test(cleanUrl)) {
      toast.error('الرابط يجب أن يبدأ بـ http:// أو https://');
      return;
    }
    try {
      const otherHost = new URL(cleanUrl).host;
      const selfHost = window.location.host;
      if (otherHost === selfHost) {
        toast.error(
          isPushMode
            ? `⛔ لا يمكن دفع البيانات من هذه البيئة إلى نفسها (${otherHost}).`
            : `⛔ لا يمكن سحب البيانات من هذه البيئة إلى نفسها (${otherHost}).`,
          { duration: 10000 });
        return;
      }
    } catch { /* ignore parse errors — server will double-check */ }

    if (!window.confirm(
      isPushMode
        ? (`⚠️ تأكيد الدفع\n\nسيتم دفع كل بيانات هذه البيئة إلى:\n${cleanUrl}\n\n`
            + '⚠️ ستُستبدل كل البيانات في البيئة الهدف.\n\nهل تريد المتابعة؟')
        : (`⚠️ تأكيد السحب\n\nسيتم سحب البيانات من:\n${cleanUrl}\n\n`
            + (wipeBeforeImport
                ? '⚠️ ستُمسح كل البيانات الحالية في هذه البيئة قبل الاستيراد.\n\n'
                : '✅ الاستيراد إضافي — لن تُمسح البيانات الحالية.\n\n')
            + `هل تريد المتابعة؟`)
    )) return;

    setMigrating(true);
    setResult(null);
    try {
      const endpoint = isPushMode
        ? '/data-migration/push-to-destination'
        : '/data-migration/pull-from-source';
      const body = isPushMode
        ? {
            destination_url: cleanUrl,
            destination_admin_email: email.trim(),
            destination_admin_password: password,
            wipe_before_import: wipeBeforeImport,
            include_uploads: includeUploads,
          }
        : {
            source_url: cleanUrl,
            source_admin_email: email.trim(),
            source_admin_password: password,
            wipe_before_import: wipeBeforeImport,
            include_uploads: includeUploads,
          };
      const r = await axiosInstance.post(endpoint, body, { timeout: 300000 });
      setResult(r.data);
      toast.success(
        isPushMode
          ? `✅ اكتمل الدفع · حجم ZIP: ${Math.round((r.data.zip_size_bytes || 0)/1024)} KB`
          : `✅ اكتمل السحب · ${r.data.docs_restored} مستند`,
        { duration: 8000 });
      onSuccess?.(r.data);
    } catch (e) {
      // Surface as much diagnostic info as possible: HTTP status, the
      // endpoint URL that was called, the response detail (or a body
      // snippet if the response was not JSON — this catches HTML 404
      // pages from proxies).
      const st  = e?.response?.status;
      const url = e?.config?.baseURL
                    ? `${e.config.baseURL}${e.config.url || ''}`
                    : (e?.config?.url || 'unknown');
      let detail = e?.response?.data?.detail
                   ?? e?.response?.data;
      if (detail && typeof detail !== 'string') {
        try { detail = JSON.stringify(detail).slice(0, 300); }
        catch { detail = String(detail).slice(0, 300); }
      }
      const bodySnip = (typeof detail === 'string'
                          ? detail
                          : (e?.response?.statusText || '')).slice(0, 300);
      const msg = st === 404
        ? (`فشل الاتصال بـ endpoint (HTTP 404) · ${url}\n`
            + `تلميح: هذا يعني أن الرابط لا يوجد على الخادم. إذا حدث ذلك على `
            + `الإنتاج، اضغط Deploy Latest أولاً. إذا حدث على المعاينة، `
            + `اعمل Hard Refresh (Ctrl+F5) لمسح ذاكرة المتصفح.`
            + (bodySnip ? `\n\nالرد: ${bodySnip}` : ''))
        : (`فشل النقل (HTTP ${st ?? '?'})\n${bodySnip || e?.message || 'خطأ غير معروف'}`);
      toast.error(msg, { duration: 15000 });
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
         data-testid="migrate-modal-overlay">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
           data-testid="migrate-modal">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center gap-3">
            <DownloadCloud className="w-6 h-6 text-blue-600" />
            <div>
              <h3 className="text-lg font-bold text-gray-800">
                🔄 نقل البيانات من بيئة أخرى
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Migrate Data from Source Environment (one-click pull)
              </p>
            </div>
          </div>
          <button onClick={handleClose} disabled={migrating}
                  className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  data-testid="migrate-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {!result && (
            <>
              {/* Mode toggle */}
              <div className="flex gap-1 p-1 bg-gray-100 rounded-lg" data-testid="migrate-mode-toggle">
                <button
                  type="button"
                  onClick={() => setMode('push')}
                  disabled={migrating}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    mode === 'push'
                      ? 'bg-white shadow text-blue-700'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  data-testid="migrate-mode-push"
                >
                  📤 دفع البيانات من هنا → إلى بيئة أخرى
                </button>
                <button
                  type="button"
                  onClick={() => setMode('pull')}
                  disabled={migrating}
                  className={`flex-1 py-2 px-3 rounded-md text-sm font-medium transition-all ${
                    mode === 'pull'
                      ? 'bg-white shadow text-blue-700'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                  data-testid="migrate-mode-pull"
                >
                  📥 سحب البيانات من بيئة أخرى → هنا
                </button>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-300 rounded-lg text-sm">
                <div className="text-blue-900">
                  {mode === 'push' ? (
                    <>
                      <strong>وضع الدفع:</strong> ستُرسل كل بيانات هذه البيئة
                      (<code>{typeof window !== 'undefined' ? window.location.host : ''}</code>)
                      إلى البيئة الهدف التي ستُدخِل رابطها أدناه. مثالياً لأول
                      نشر إلى الإنتاج: افتح هذا الزر <b>على المعاينة</b>
                      وأدخل رابط الإنتاج كهدف.
                    </>
                  ) : (
                    <>
                      <strong>وضع السحب:</strong> ستُحضِر البيانات من بيئة
                      المصدر التي ستُدخِل رابطها أدناه إلى هذه البيئة الحالية.
                      استخدم هذا الوضع <b>على البيئة الهدف</b>.
                    </>
                  )}
                </div>
              </div>

              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-yellow-600 mt-0.5" />
                  <div className="text-yellow-800">
                    <strong>خاص بأول نشر إلى الإنتاج:</strong> أدخل رابط بيئة المعاينة
                    التي تحوي بياناتك الحقيقية. سيتم تسجيل الدخول تلقائياً، تنزيل
                    كامل قاعدة البيانات + المرفقات، ثم استعادتها هنا.
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  {mode === 'push' ? 'رابط الهدف (Destination URL) *' : 'رابط المصدر (Source URL) *'}
                </label>
                <input
                  type="url"
                  value={sourceUrl}
                  onChange={e => setSourceUrl(e.target.value)}
                  placeholder={mode === 'push'
                    ? 'https://accounting-pro-39.emergent.host'
                    : 'https://your-preview-app.preview.emergentagent.com'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                  disabled={migrating}
                  data-testid="migrate-source-url-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  {mode === 'push'
                    ? 'رابط بيئة الإنتاج التي تريد استقبال البيانات.'
                    : 'رابط بيئة المعاينة كاملاً (بدون شرطة في النهاية).'}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {mode === 'push' ? 'بريد المسؤول على الهدف *' : 'بريد المسؤول على المصدر *'}
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                    disabled={migrating}
                    data-testid="migrate-email-input"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    كلمة المرور *
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 text-sm"
                    disabled={migrating}
                    data-testid="migrate-password-input"
                  />
                </div>
              </div>

              <div className="space-y-2 border-t pt-3">
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={wipeBeforeImport}
                    onChange={e => setWipeBeforeImport(e.target.checked)}
                    disabled={migrating}
                    className="rounded"
                    data-testid="migrate-wipe-checkbox"
                  />
                  <span>
                    مسح البيانات الحالية قبل الاستيراد
                    <span className="text-xs text-red-600 mr-1">(موصى به لأول نشر)</span>
                  </span>
                </label>
                <label className="flex items-center gap-2 text-sm cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeUploads}
                    onChange={e => setIncludeUploads(e.target.checked)}
                    disabled={migrating}
                    className="rounded"
                    data-testid="migrate-uploads-checkbox"
                  />
                  <span>استعادة المرفقات (الصور، PDF، الملفات)</span>
                </label>
              </div>

              {migrating && (
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
                  <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                  <span className="text-sm text-blue-800">
                    جارٍ النقل… قد يستغرق ذلك دقيقة أو أكثر لقاعدة بيانات كبيرة.
                  </span>
                </div>
              )}
            </>
          )}

          {result && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg space-y-3"
                 data-testid="migrate-result">
              <div className="flex items-center gap-2 text-green-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" />
                اكتمل النقل بنجاح!
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-white rounded p-2 border border-green-100">
                  <div className="text-[10px] text-gray-500">مستندات مستعادة</div>
                  <div className="font-mono font-bold text-lg">{result.docs_restored}</div>
                </div>
                <div className="bg-white rounded p-2 border border-green-100">
                  <div className="text-[10px] text-gray-500">مجموعات</div>
                  <div className="font-mono font-bold text-lg">{result.collections_restored?.length}</div>
                </div>
                <div className="bg-white rounded p-2 border border-green-100">
                  <div className="text-[10px] text-gray-500">مرفقات</div>
                  <div className="font-mono font-bold text-lg">{result.uploads_restored}</div>
                </div>
                <div className="bg-white rounded p-2 border border-green-100">
                  <div className="text-[10px] text-gray-500">مجموعات متجاوزة</div>
                  <div className="font-mono font-bold text-lg">{result.collections_skipped?.length ?? 0}</div>
                </div>
              </div>
              <p className="text-xs text-gray-700 bg-yellow-50 p-2 rounded border border-yellow-200">
                ⚠️ <strong>سجّل الخروج والدخول مجدداً</strong> — تم استبدال المسؤول
                الافتراضي بالمستخدمين المستعادين من المصدر.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 p-4 border-t bg-gray-50">
          <button
            onClick={handleClose}
            disabled={migrating}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50"
            data-testid="migrate-cancel-btn"
          >
            {result ? 'إغلاق' : 'إلغاء'}
          </button>
          {!result && (
            <button
              onClick={runMigration}
              disabled={migrating || !sourceUrl.trim() || !email.trim() || !password}
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-md disabled:opacity-50 flex items-center gap-2"
              data-testid="migrate-run-btn"
            >
              {migrating
                ? <><Loader2 className="w-4 h-4 animate-spin" /> جارٍ النقل…</>
                : (mode === 'push'
                    ? <><DownloadCloud className="w-4 h-4 rotate-180" /> ابدأ الدفع</>
                    : <><DownloadCloud className="w-4 h-4" /> ابدأ السحب</>)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default MigrateFromPreviewModal;
